/*
 * chart.js — the profile under the map: elevation, and beneath it one band per
 * recorded channel (speed or pace, heart rate, cadence, power, temperature,
 * grade), all on one x axis (distance or time).
 *
 *   var ch = GpsChart.create(host, { onHover(i|null), onSelect(from, to)|onSelect(null), onPick(i), colors() })
 *   ch.set({ x, xKind:'dist'|'time', fmtX, xStep, fmtTip, aria, breaks,
 *            panels:[{ key, label, v, color, fmt, tick, fill, invert, robust, zero, minSpan, w, minH, shade }],
 *            marks:[{ from, to, label, color }] })
 *   ch.cursor(i) / ch.select(from, to) / ch.highlight(from, to) / ch.selectMode(on) / ch.redraw()
 *   ch.minHeight()  — the least height the current bands need to stay readable
 *
 * The chart FITS ITS HOST: the host has a height (the page sets it and the
 * person can drag it), and the bands share that height by weight — elevation
 * gets the most. Until 25.09.2026 every band had a fixed height and the chart
 * grew to ~700 px with all channels on, pushing everything else off the screen.
 *
 * One canvas, drawn per pixel column: when a column holds several fixes the
 * band shows their min–max envelope, so a 1 Hz heart-rate trace at 900 px
 * width stays honest (its spikes are visible) instead of being averaged away.
 * Mouse: hover scrubs, drag selects. Touch: drag scrubs; with selectMode on,
 * drag selects. Keyboard: ←/→ move the cursor, Shift extends a selection.
 */
(function () {
    'use strict';

    // Diverging grade palette — the same stops as the map (map-layer.js DIV):
    // down is blue, flat is neutral, up runs to red.
    var GRADE_STOPS = [[-15, [30, 90, 200]], [-5, [80, 160, 220]], [0, [150, 150, 150]], [4, [240, 170, 30]], [8, [235, 110, 25]], [15, [200, 20, 30]]];
    function gradeRgb(g) {
        if (!(g === g)) return null;
        if (g <= GRADE_STOPS[0][0]) return GRADE_STOPS[0][1];
        for (var i = 1; i < GRADE_STOPS.length; i++) {
            if (g <= GRADE_STOPS[i][0]) {
                var a = GRADE_STOPS[i - 1], b = GRADE_STOPS[i], f = (g - a[0]) / (b[0] - a[0]);
                return [a[1][0] + (b[1][0] - a[1][0]) * f, a[1][1] + (b[1][1] - a[1][1]) * f, a[1][2] + (b[1][2] - a[1][2]) * f];
            }
        }
        return GRADE_STOPS[GRADE_STOPS.length - 1][1];
    }

    function create(host, opts) {
        opts = opts || {};
        var cv = document.createElement('canvas');
        cv.className = 'gt-chart-cv';
        cv.setAttribute('tabindex', '0');
        cv.setAttribute('role', 'img');
        var tip = document.createElement('div');
        tip.className = 'gt-chart-tip';
        tip.hidden = true;
        host.appendChild(cv);
        host.appendChild(tip);
        var ctx = cv.getContext('2d');
        var data = null, cur = -1, sel = null, hl = null, dragging = null, selMode = false;
        var W = 0, H = 0, dpr = 1;
        var PADL = 44, PADR = 10, AXIS = 20, GAP = 5, TOP = 3;
        var layout = [];

        function colors() {
            return opts.colors ? opts.colors() : { text: '#cfd3dc', muted: '#8b93a3', grid: 'rgba(255,255,255,.08)', sel: 'rgba(255,45,122,.16)', cursor: '#ff2d7a', bg: 'transparent', pill: 'rgba(17,19,24,.72)' };
        }

        function panelMin(p) { return p.minH || (p.key === 'ele' ? 48 : 26); }
        function needH() {
            var ps = data ? data.panels : [];
            if (!ps.length) return 120;
            var h = AXIS + TOP;
            ps.forEach(function (p, i) { h += panelMin(p) + (i ? GAP : 0); });
            return h;
        }

        function size() {
            var r = host.getBoundingClientRect();
            W = Math.max(200, Math.round(r.width));
            // the host owns the height; a host without one (old markup) falls
            // back to what the bands need
            var hh = Math.round(r.height);
            H = Math.max(needH(), hh > 40 ? hh : 180);
            dpr = Math.min(window.devicePixelRatio || 1, 2);
            cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
            cv.style.width = W + 'px'; cv.style.height = H + 'px';
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        }

        function xAt(i) { var x = data.x; return PADL + (x[i] - data.x0) / (data.x1 - data.x0 || 1) * (W - PADL - PADR); }
        function idxAtPx(px) {
            var x = data.x, n = x.length;
            var v = data.x0 + (px - PADL) / (W - PADL - PADR) * (data.x1 - data.x0);
            var lo = 0, hi = n - 1;
            while (lo < hi) { var m = (lo + hi) >> 1; if (x[m] < v) lo = m + 1; else hi = m; }
            if (lo > 0 && Math.abs(x[lo - 1] - v) <= Math.abs(x[lo] - v)) lo--;
            return lo;
        }

        function domain(p) {
            var mn = Infinity, mx = -Infinity, v = p.v;
            for (var i = 0; i < v.length; i++) { var a = v[i]; if (a === a) { if (a < mn) mn = a; if (a > mx) mx = a; } }
            if (!isFinite(mn)) return null;
            if (p.robust) { // speed: GPS spikes should not flatten the whole band
                var s = [];
                for (var k = 0; k < v.length; k += Math.max(1, Math.floor(v.length / 4000))) if (v[k] === v[k]) s.push(v[k]);
                s.sort(function (a, b) { return a - b; });
                mx = Math.min(mx, s[Math.floor(s.length * 0.995)] * 1.1 || mx);
            }
            if (p.zero) mn = Math.min(0, mn);
            if (mx - mn < (p.minSpan || 1)) { var c = (mx + mn) / 2; mn = c - (p.minSpan || 1) / 2; mx = c + (p.minSpan || 1) / 2; }
            var pad = (mx - mn) * 0.08;
            return [mn - (p.zero ? 0 : pad), mx + pad];
        }

        function nice(span, count) {
            var raw = span / Math.max(1, count), mag = Math.pow(10, Math.floor(Math.log10(raw))), f = raw / mag;
            return (f < 1.5 ? 1 : f < 3 ? 2 : f < 7 ? 5 : 10) * mag;
        }

        /* Heights by weight within what the host gives: elevation 2.4, every
         * other band 1; each band keeps its readable minimum. */
        function bandHeights() {
            var ps = data.panels, n = ps.length;
            var avail = H - AXIS - TOP - GAP * Math.max(0, n - 1);
            var wsum = 0;
            ps.forEach(function (p) { wsum += p.w || (p.key === 'ele' ? 2.4 : 1); });
            var hs = ps.map(function (p) { return Math.max(panelMin(p), Math.floor(avail * (p.w || (p.key === 'ele' ? 2.4 : 1)) / wsum)); });
            // rounding and minimums may overshoot by a few px: take it from the tallest
            var over = hs.reduce(function (a, b) { return a + b; }, 0) - avail;
            while (over > 0) {
                var big = 0;
                for (var i = 1; i < n; i++) if (hs[i] - panelMin(ps[i]) > hs[big] - panelMin(ps[big])) big = i;
                if (hs[big] <= panelMin(ps[big])) break;
                hs[big]--; over--;
            }
            return hs;
        }

        function pill(text, x, y, color) {
            var C = colors();
            ctx.font = '700 12px system-ui,-apple-system,Segoe UI,sans-serif';
            var w = ctx.measureText(text).width + 20;
            ctx.fillStyle = C.pill || 'rgba(17,19,24,.72)';
            roundRect(x, y, w, 18, 9); ctx.fill();
            ctx.fillStyle = color;
            ctx.beginPath(); ctx.arc(x + 8, y + 9, 3.5, 0, Math.PI * 2); ctx.fill();
            ctx.fillStyle = C.pillText || '#fff';
            ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
            ctx.fillText(text, x + 15, y + 9.5);
        }
        function roundRect(x, y, w, h, r) {
            ctx.beginPath();
            ctx.moveTo(x + r, y); ctx.lineTo(x + w - r, y); ctx.arcTo(x + w, y, x + w, y + r, r);
            ctx.lineTo(x + w, y + h - r); ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
            ctx.lineTo(x + r, y + h); ctx.arcTo(x, y + h, x, y + h - r, r);
            ctx.lineTo(x, y + r); ctx.arcTo(x, y, x + r, y, r);
            ctx.closePath();
        }

        function draw() {
            if (!data) { ctx.clearRect(0, 0, W, H); return; }
            var C = colors();
            ctx.clearRect(0, 0, W, H);
            var y = TOP;
            layout = [];
            var breaks = {};
            (data.breaks || []).forEach(function (b) { breaks[b] = 1; });
            var n = data.x.length, plotW = W - PADL - PADR;
            var hs = bandHeights();
            // selection band behind everything
            if (sel) {
                var s0 = xAt(sel.from), s1 = xAt(sel.to);
                ctx.fillStyle = C.sel;
                ctx.fillRect(Math.min(s0, s1), 0, Math.max(1, Math.abs(s1 - s0)), H - AXIS);
            }
            if (hl && !sel) {
                var h0 = xAt(hl.from), h1 = xAt(hl.to);
                ctx.fillStyle = C.hl || 'rgba(255,170,40,.14)';
                ctx.fillRect(Math.min(h0, h1), 0, Math.max(1, Math.abs(h1 - h0)), H - AXIS);
            }
            data.panels.forEach(function (p, pi) {
                var h = hs[pi], dom = domain(p);
                layout.push({ p: p, y: y, h: h, dom: dom });
                if (!dom) { y += h + GAP; return; }
                var step = nice(dom[1] - dom[0], h > 90 ? 4 : h > 44 ? 2 : 1);
                ctx.font = '12px system-ui,-apple-system,Segoe UI,sans-serif';
                ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
                ctx.strokeStyle = C.grid; ctx.lineWidth = 1;
                var ticks = 0;
                for (var t = Math.ceil(dom[0] / step) * step; t <= dom[1] && ticks < 6; t += step) {
                    var ty = Math.round(y + h - (t - dom[0]) / (dom[1] - dom[0]) * h) + 0.5;
                    if (ty < y + 7 || ty > y + h - 4) continue;
                    ticks++;
                    ctx.beginPath(); ctx.moveTo(PADL, ty); ctx.lineTo(W - PADR, ty); ctx.stroke();
                    ctx.fillStyle = C.muted;
                    ctx.fillText(p.tick ? p.tick(t) : String(Math.round(t * 10) / 10), PADL - 5, ty);
                }
                // series by pixel column
                var toY = function (v) { var r = (v - dom[0]) / (dom[1] - dom[0]); if (p.invert) r = 1 - r; return y + h - r * h; };
                var i;
                var lastCol = -1, colMin = 0, colMax = 0, colLast = 0, colI = 0;
                var pts = []; // [x, yMin, yMax, yLast, index]
                for (i = 0; i < n; i++) {
                    var v = p.v[i];
                    var px = xAt(i);
                    var col = Math.floor(px);
                    if (v !== v) { if (lastCol >= 0) { pts.push([lastCol, colMin, colMax, colLast, colI]); lastCol = -1; } pts.push(null); continue; }
                    if (breaks[i] && i > 0) { if (lastCol >= 0) { pts.push([lastCol, colMin, colMax, colLast, colI]); lastCol = -1; } pts.push(null); }
                    var yy = toY(v);
                    if (col !== lastCol) {
                        if (lastCol >= 0) pts.push([lastCol, colMin, colMax, colLast, colI]);
                        lastCol = col; colMin = colMax = colLast = yy; colI = i;
                    } else { if (yy < colMin) colMin = yy; if (yy > colMax) colMax = yy; colLast = yy; }
                }
                if (lastCol >= 0) pts.push([lastCol, colMin, colMax, colLast, colI]);
                ctx.save();
                ctx.beginPath(); ctx.rect(PADL, y, plotW + 1, h + 1); ctx.clip();
                // fill (elevation): either a vertical fade of the band colour, or
                // each pixel column in the colour of the local grade
                if (p.fill) {
                    if (p.shade) {
                        var prevX = null;
                        pts.forEach(function (q) {
                            if (!q) { prevX = null; return; }
                            var g = p.shade[q[4]];
                            var c = gradeRgb(g);
                            ctx.fillStyle = c ? 'rgba(' + Math.round(c[0]) + ',' + Math.round(c[1]) + ',' + Math.round(c[2]) + ',.78)' : hexA(p.color, 0.3);
                            var x0 = prevX == null ? q[0] : prevX + 1;
                            ctx.fillRect(x0, (q[1] + q[2]) / 2, q[0] - x0 + 1, y + h - (q[1] + q[2]) / 2);
                            prevX = q[0];
                        });
                    } else {
                        var grad = ctx.createLinearGradient(0, y, 0, y + h);
                        grad.addColorStop(0, hexA(p.color, 0.45));
                        grad.addColorStop(1, hexA(p.color, 0.05));
                        ctx.fillStyle = grad;
                        var run = [];
                        var flush = function () {
                            if (run.length < 2) { run = []; return; }
                            ctx.beginPath();
                            ctx.moveTo(run[0][0], y + h);
                            run.forEach(function (q) { ctx.lineTo(q[0] + 0.5, (q[1] + q[2]) / 2); });
                            ctx.lineTo(run[run.length - 1][0] + 0.5, y + h);
                            ctx.closePath(); ctx.fill();
                            run = [];
                        };
                        pts.forEach(function (q) { if (!q) flush(); else run.push(q); });
                        flush();
                    }
                }
                ctx.strokeStyle = p.shade ? (C.lineOnShade || C.text) : p.color; ctx.lineWidth = 1.6; ctx.lineJoin = 'round';
                ctx.beginPath();
                var started = false;
                pts.forEach(function (q) {
                    if (!q) { started = false; return; }
                    var cx = q[0] + 0.5;
                    if (!started) { ctx.moveTo(cx, q[1]); started = true; }
                    else ctx.lineTo(cx, q[1]);
                    if (q[2] - q[1] > 1) ctx.lineTo(cx, q[2]);
                    ctx.lineTo(cx, q[3]);
                });
                ctx.stroke();
                // marks (climbs) on the elevation band: a tinted span + its tag
                if (p.key === 'ele' && data.marks && data.marks.length) {
                    ctx.font = '800 12px system-ui,-apple-system,Segoe UI,sans-serif';
                    ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
                    data.marks.forEach(function (m) {
                        var a = xAt(m.from), b = xAt(m.to);
                        if (b - a < 2) return;
                        ctx.fillStyle = hexA(m.color || '#f76b15', 0.9);
                        ctx.fillRect(a, y + h - 3, b - a, 3);
                        if (!m.label || b - a < 22 || h < 44) return;
                        var tw = ctx.measureText(m.label).width + 10;
                        var cx2 = Math.max(PADL + tw / 2, Math.min(W - PADR - tw / 2, (a + b) / 2));
                        ctx.fillStyle = hexA(m.color || '#f76b15', 0.95);
                        roundRect(cx2 - tw / 2, y + h - 22, tw, 16, 8); ctx.fill();
                        ctx.fillStyle = '#fff';
                        ctx.fillText(m.label, cx2, y + h - 13.5);
                    });
                }
                ctx.restore();
                // the band's name: a small pill in its corner, only where there is room
                if (h >= 40 && data.panels.length > 1) pill(p.label, PADL + 5, y + 4, p.color);
                y += h + GAP;
            });
            // x axis
            var ay = H - AXIS;
            ctx.strokeStyle = C.grid; ctx.beginPath(); ctx.moveTo(PADL, ay + 0.5); ctx.lineTo(W - PADR, ay + 0.5); ctx.stroke();
            ctx.fillStyle = C.muted; ctx.font = '12px system-ui,-apple-system,Segoe UI,sans-serif';
            ctx.textAlign = 'center'; ctx.textBaseline = 'top';
            var span = data.x1 - data.x0;
            var xs = data.xStep ? data.xStep(span, Math.max(2, Math.floor(plotW / 90))) : nice(span, Math.max(2, Math.floor(plotW / 90)));
            for (var xv = Math.ceil(data.x0 / xs) * xs; xv <= data.x1 + 1e-9; xv += xs) {
                var xp = PADL + (xv - data.x0) / (span || 1) * plotW;
                if (xp < PADL + 8) continue;
                ctx.fillText(data.fmtX ? data.fmtX(xv, xs) : String(xv), xp, ay + 4);
            }
            if (data.xUnit) { ctx.textAlign = 'left'; ctx.fillText(data.xUnit, 4, ay + 4); }
            // cursor
            if (cur >= 0 && cur < n) {
                var cx = Math.round(xAt(cur)) + 0.5;
                ctx.strokeStyle = C.cursor; ctx.lineWidth = 1;
                ctx.beginPath(); ctx.moveTo(cx, 0); ctx.lineTo(cx, H - AXIS); ctx.stroke();
                layout.forEach(function (L) {
                    if (!L.dom) return;
                    var v = L.p.v[cur];
                    if (v !== v) return;
                    var r = (v - L.dom[0]) / (L.dom[1] - L.dom[0]); if (L.p.invert) r = 1 - r;
                    var cy = L.y + L.h - r * L.h;
                    ctx.beginPath(); ctx.arc(cx, cy, 3.5, 0, Math.PI * 2);
                    ctx.fillStyle = L.p.color; ctx.fill();
                    ctx.lineWidth = 1.5; ctx.strokeStyle = C.bgSolid || '#fff'; ctx.stroke();
                });
            }
        }

        function hexA(hex, a) {
            var h = String(hex).replace('#', '');
            if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
            var r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
            return 'rgba(' + r + ',' + g + ',' + b + ',' + a + ')';
        }

        function showTip(i, px) {
            if (!data || i < 0) { tip.hidden = true; return; }
            var rows = [];
            rows.push('<div class="gt-tip-x">' + esc(data.fmtTip ? data.fmtTip(i) : '') + '</div>');
            data.panels.forEach(function (p) {
                var v = p.v[i];
                if (v !== v) return;
                rows.push('<div class="gt-tip-r"><i style="background:' + p.color + '"></i>' + esc(p.label) + ' <b>' + esc(p.fmt ? p.fmt(v) : String(v)) + '</b></div>');
                if (p.shade && p.shade[i] === p.shade[i] && data.gradeLabel) rows.push('<div class="gt-tip-r"><i style="background:transparent"></i>' + esc(data.gradeLabel) + ' <b>' + esc((Math.round(p.shade[i] * 10) / 10) + '%') + '</b></div>');
            });
            tip.innerHTML = rows.join('');
            tip.hidden = false;
            var tw = tip.offsetWidth;
            var left = px + 14;
            if (left + tw > W - 4) left = px - tw - 14;
            tip.style.left = Math.max(4, left) + 'px';
            tip.style.top = '6px';
        }
        function esc(s) { return String(s).replace(/[&<>"]/g, function (c) { return c === '&' ? '&amp;' : c === '<' ? '&lt;' : c === '>' ? '&gt;' : '&quot;'; }); }

        function localX(e) { var r = cv.getBoundingClientRect(); return e.clientX - r.left; }
        cv.addEventListener('pointermove', function (e) {
            if (!data) return;
            var px = Math.max(PADL, Math.min(W - PADR, localX(e)));
            var i = idxAtPx(px);
            if (dragging) {
                sel = { from: Math.min(dragging.start, i), to: Math.max(dragging.start, i) };
            }
            cur = i;
            draw();
            showTip(i, px);
            if (opts.onHover) opts.onHover(i);
        });
        cv.addEventListener('pointerleave', function () {
            if (dragging) return;
            tip.hidden = true;
            if (opts.onHover) opts.onHover(null);
        });
        cv.addEventListener('pointerdown', function (e) {
            if (!data) return;
            var canSelect = e.pointerType === 'mouse' ? e.button === 0 : selMode;
            var i = idxAtPx(Math.max(PADL, Math.min(W - PADR, localX(e))));
            cur = i;
            if (canSelect) {
                dragging = { start: i, x: e.clientX };
                try { cv.setPointerCapture(e.pointerId); } catch (_) {}
            } else if (e.pointerType !== 'mouse') {
                try { cv.setPointerCapture(e.pointerId); } catch (_) {}
            }
            draw();
            if (opts.onHover) opts.onHover(i);
        });
        function endDrag(e) {
            if (!dragging) return;
            var moved = Math.abs(e.clientX - dragging.x) > 4;
            dragging = null;
            if (moved && sel && sel.to > sel.from) { if (opts.onSelect) opts.onSelect(sel.from, sel.to); }
            else { sel = null; draw(); if (opts.onSelect) opts.onSelect(null); if (opts.onPick) opts.onPick(cur); }
        }
        cv.addEventListener('pointerup', endDrag);
        cv.addEventListener('pointercancel', function () { dragging = null; });
        cv.addEventListener('keydown', function (e) {
            if (!data) return;
            var n = data.x.length, step = Math.max(1, Math.round(n / 200));
            if (e.shiftKey) step = Math.max(1, Math.round(n / 20));
            var k = e.key, prev = cur < 0 ? 0 : cur;
            if (k === 'ArrowRight' || k === 'ArrowLeft') {
                var nx = Math.max(0, Math.min(n - 1, prev + (k === 'ArrowRight' ? step : -step)));
                if (e.shiftKey) {
                    var a = sel ? (prev === sel.to ? sel.from : sel.to) : prev;
                    sel = { from: Math.min(a, nx), to: Math.max(a, nx) };
                    if (opts.onSelect) opts.onSelect(sel.from, sel.to);
                }
                cur = nx;
            } else if (k === 'Home') cur = 0;
            else if (k === 'End') cur = n - 1;
            else if (k === 'Escape') { sel = null; if (opts.onSelect) opts.onSelect(null); }
            else return;
            e.preventDefault();
            draw();
            showTip(cur, xAt(cur));
            if (opts.onHover) opts.onHover(cur);
        });
        cv.addEventListener('blur', function () { tip.hidden = true; });

        var ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(function () { size(); draw(); }) : null;
        if (ro) ro.observe(host);
        size();

        return {
            set: function (d) {
                data = d;
                if (data) {
                    var x = data.x, x0 = Infinity, x1 = -Infinity;
                    for (var i = 0; i < x.length; i++) { var v = x[i]; if (v === v) { if (v < x0) x0 = v; if (v > x1) x1 = v; } }
                    data.x0 = x0; data.x1 = x1;
                    if (data.aria) cv.setAttribute('aria-label', data.aria);
                }
                if (cur >= (data ? data.x.length : 0)) cur = -1;
                sel = null; hl = null;
                size(); draw();
            },
            cursor: function (i) { cur = i == null ? -1 : i; draw(); if (i == null) tip.hidden = true; },
            select: function (a, b) { sel = a == null ? null : { from: Math.min(a, b), to: Math.max(a, b) }; draw(); },
            highlight: function (a, b) { hl = a == null ? null : { from: Math.min(a, b), to: Math.max(a, b) }; draw(); },
            selectMode: function (on) { selMode = !!on; },
            redraw: function () { size(); draw(); },
            minHeight: function () { return needH(); },
            canvas: cv
        };
    }

    window.GpsChart = { create: create, gradeRgb: gradeRgb };
})();
